import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, Loader2, CheckCircle2 } from "lucide-react";
import { postCatalogueLead, catalogueDownloadUrl } from "@/lib/api";
import { toast } from "sonner";
import { EISG } from "@/constants/testIds";

const CatalogueDialog = ({ trigger, triggerTestId }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", organisation: "", country: "" });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Veuillez renseigner nom et email.");
      return;
    }
    setLoading(true);
    try {
      await postCatalogueLead(form);
      setDone(true);
      toast.success("Téléchargement disponible. Un email vous a également été envoyé.");
      // Open the catalogue PDF in a new tab
      window.open(catalogueDownloadUrl(), "_blank", "noopener");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez ou écrivez-nous à contact@bridgedatum.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setDone(false); } }}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid={triggerTestId || EISG.heroCtaCatalogue} className="btn-premium btn-premium-gold">
            <Download size={16} /> Télécharger le catalogue
          </button>
        )}
      </DialogTrigger>
      <DialogContent data-testid={EISG.catalogueDialog} className="sm:max-w-[480px] bg-white border border-[#0A192F]/15 rounded-none">
        <DialogHeader>
          <div className="section-num mb-2">CATALOGUE · 2026 — 2027</div>
          <DialogTitle className="font-eisg text-2xl text-[#0A192F] tracking-tight">Recevoir le catalogue EISG</DialogTitle>
          <DialogDescription className="text-sm text-[#0A192F]/70">
            Renseignez vos coordonnées pour télécharger immédiatement le catalogue exécutif et le recevoir par email.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-6 text-center space-y-3">
            <CheckCircle2 className="mx-auto text-[#0A192F]" size={40} />
            <p className="text-sm text-[#0A192F]">
              Le téléchargement a démarré dans un nouvel onglet. Un email vous a été envoyé à <strong>{form.email}</strong>.
            </p>
            <a
              href={catalogueDownloadUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0A192F] underline"
            >
              <Download size={14} /> Re-télécharger
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            <div>
              <Label htmlFor="cat-name" className="text-xs uppercase tracking-wider font-semibold">Nom et prénom *</Label>
              <Input data-testid={EISG.catalogueDialogName} id="cat-name" value={form.full_name} onChange={update("full_name")} required className="mt-1 rounded-none border-[#0A192F]/20" />
            </div>
            <div>
              <Label htmlFor="cat-email" className="text-xs uppercase tracking-wider font-semibold">Email *</Label>
              <Input data-testid={EISG.catalogueDialogEmail} id="cat-email" type="email" value={form.email} onChange={update("email")} required className="mt-1 rounded-none border-[#0A192F]/20" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cat-org" className="text-xs uppercase tracking-wider font-semibold">Organisation</Label>
                <Input data-testid={EISG.catalogueDialogOrg} id="cat-org" value={form.organisation} onChange={update("organisation")} className="mt-1 rounded-none border-[#0A192F]/20" />
              </div>
              <div>
                <Label htmlFor="cat-country" className="text-xs uppercase tracking-wider font-semibold">Pays</Label>
                <Input data-testid={EISG.catalogueDialogCountry} id="cat-country" value={form.country} onChange={update("country")} className="mt-1 rounded-none border-[#0A192F]/20" />
              </div>
            </div>
            <Button
              data-testid={EISG.catalogueDialogSubmit}
              type="submit"
              disabled={loading}
              className="w-full bg-[#0A192F] hover:bg-[#001529] text-white rounded-none h-12 font-semibold tracking-wider text-xs uppercase"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Download size={16} className="mr-2" />}
              Télécharger maintenant
            </Button>
            <p className="text-[11px] text-[#0A192F]/60 leading-relaxed">
              Vos données restent confidentielles et ne servent qu'à l'envoi du catalogue et aux communications EISG.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CatalogueDialog;
